import { useLocalStorage } from '@bv/composables/common/local-storage';
import { useToast } from '@bv/composables/common/toast';
import { MAINTENANCE_START_MS } from '@bv/constants/maintenance/dates';

/** Single dismiss flag. `useLocalStorage` drops it when the maintenance window starts. */
const MAINTENANCE_NOTICE_STORAGE_KEY = 'bv.maintenance-notice';

/**
 * Whether `now` is still before the maintenance window starts.
 * @param now - Timestamp in milliseconds. Defaults to the current time.
 */
const isBeforeMaintenanceStart = (now: number = Date.now()): boolean => now < MAINTENANCE_START_MS;

/**
 * Seconds left until the maintenance window starts.
 * Passed to `useLocalStorage` so the dismiss flag expires at that moment.
 * @param now - Timestamp in milliseconds. Defaults to the current time.
 */
const secondsUntilMaintenanceStart = (now: number = Date.now()): number =>
  Math.max(0, (MAINTENANCE_START_MS - now) / 1000);

/**
 * Hides the notice until the next login, or until the maintenance window starts.
 */
const dismissMaintenanceNotice = (): void => {
  useLocalStorage().set(MAINTENANCE_NOTICE_STORAGE_KEY, true, secondsUntilMaintenanceStart());
};

/**
 * Makes the notice eligible to show again. Called on each new login.
 */
export const clearMaintenanceNotice = (): void => {
  useLocalStorage().remove(MAINTENANCE_NOTICE_STORAGE_KEY);
};

/**
 * Whether the warning should be shown at `now`.
 * Hidden after dismiss while the storage timer is still running.
 * @param now - Timestamp in milliseconds. Defaults to the current time.
 */
const shouldShowMaintenanceNotice = (now: number = Date.now()): boolean =>
  isBeforeMaintenanceStart(now) && useLocalStorage().get(MAINTENANCE_NOTICE_STORAGE_KEY) === null;

/**
 * Shows the scheduled-maintenance warning until the user closes it.
 * Closing it hides the notice until the next login. Nothing is shown once the
 * maintenance window has started.
 */
export const showMaintenanceNotice = (): void => {
  if (!shouldShowMaintenanceNotice()) {
    return;
  }

  useToast().warning('global.maintenance.notice.message_trans', {
    position: 'top-center',
    autoClose: false,
    dedupe: 'scheduled-maintenance',
    onClose: () => {
      dismissMaintenanceNotice();
    },
  });
};
